import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import YouTube from "react-youtube";
import "./YouTubeCard.scss";

const YT_CONSENT_KEY = "yt_consent";
let cachedConsent = null;

function hasYouTubeConsent() {
  if (cachedConsent !== null) return cachedConsent;
  if (typeof window === "undefined") return false;
  try {
    const value = window.localStorage.getItem(YT_CONSENT_KEY);
    cachedConsent = value === "true";
    return cachedConsent;
  } catch {
    return false;
  }
}

function setYouTubeConsentAccepted() {
  cachedConsent = true;
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(YT_CONSENT_KEY, "true");
  } catch {
  }
}

function useIsMobile(breakpoint) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);

    const handleChange = (e) => {
      setIsMobile(e.matches);
    };

    setIsMobile(mediaQuery.matches);

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, [breakpoint]);

  return isMobile;
}

export function YouTubeConsentModal({ open, onAccept, onCancel }) {
  if (!open) return null;

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && onCancel) {
      onCancel();
    }
  };

  const modal = (
    <div
      className="yt-consent-overlay"
      aria-hidden={!open}
      onClick={handleOverlayClick}
    >
      <div
        className="yt-consent-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="yt-consent-title"
      >
        <h3 id="yt-consent-title">Before you continue</h3>
        <p>This feature uses YouTube API Services.</p>
        <p>
          By clicking Continue, you agree to our{" "}
          <Link to="/privacy" onClick={onCancel}>
            Privacy Policy
          </Link>
          .
        </p>
        <div className="yt-consent-actions">
          <button
            type="button"
            className="yt-consent-btn yt-consent-btn-primary"
            onClick={onAccept}
          >
            Continue
          </button>
          <button
            type="button"
            className="yt-consent-btn yt-consent-btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return modal;
  }

  return createPortal(modal, document.body);
}



export default function YouTubeCard({ videoId, channelCountry, channelName, onMobileClick, isPlaying, onEnded, onPlay }) {
  const isMobile = useIsMobile(768);
  const [showModal, setShowModal] = useState(false);
  const [localShowIframe, setLocalShowIframe] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  // Store the player instance so we can unmute on play and pause before teardown
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  // Touch tracking: bypass react-slick swipe interception for taps
  const touchStartRef = useRef(null);
  const touchHandledRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            try {
              const p = playerRef.current;
              if (p && typeof p.pauseVideo === "function") {
                const pausePromise = p.pauseVideo();
                if (pausePromise && pausePromise.catch) pausePromise.catch(() => {});
              }
            } catch {}
          }
        });
      },
      { threshold: 0 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const isControlled = typeof isPlaying !== "undefined";
  const showIframe = isControlled ? isPlaying : localShowIframe;

  useEffect(() => {
    const handleYtPlay = (e) => {
      if (e.detail !== videoId) {
        // Pause before hiding to prevent audio bleed on slow devices
        try {
          const p = playerRef.current;
          if (p && typeof p.pauseVideo === "function") {
            const pausePromise = p.pauseVideo();
            if (pausePromise && pausePromise.catch) pausePromise.catch(() => {});
          }
        } catch { }
        if (!isControlled) setLocalShowIframe(false);
      }
    };
    window.addEventListener("yt-play", handleYtPlay);
    return () => window.removeEventListener("yt-play", handleYtPlay);
  }, [videoId, isControlled]);

  // When controlled mode turns off (isPlaying goes false), pause immediately
  useEffect(() => {
    if (isControlled && !isPlaying) {
      try {
        const p = playerRef.current;
        if (p && typeof p.pauseVideo === "function") {
          const pausePromise = p.pauseVideo();
          if (pausePromise && pausePromise.catch) pausePromise.catch(() => {});
        }
      } catch { }
    }
  }, [isControlled, isPlaying]);

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  const shouldShowIframe = !isMobile && showIframe;

  const openInNewTab = () => {
    if (typeof window === "undefined") return;
    const url = `https://www.youtube.com/watch?v=${videoId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const runAction = () => {
    if (isMobile) {
      if (onMobileClick) {
        onMobileClick(videoId);
      } else {
        openInNewTab();
      }
    } else {
      // Only set local state when NOT controlled — avoids dual-player race
      if (!isControlled) setLocalShowIframe(true);
      if (onPlay) onPlay();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("yt-play", { detail: videoId }));
      }
    }
  };

  const handleStateChange = (e) => {
    const state = e?.data;
    const Y = window.YT?.PlayerState;
    const isNowPlaying = Y ? state === Y.PLAYING : state === 1;
    const isEnded = Y ? state === Y.ENDED : state === 0;

    if (isNowPlaying) {
      // Auto-unmute when the user explicitly presses play
      try {
        const p = playerRef.current;
        if (p && typeof p.unMute === "function") {
          const unmutePromise = p.unMute();
          if (unmutePromise && unmutePromise.catch) unmutePromise.catch(() => {});
          const volPromise = p.setVolume(100);
          if (volPromise && volPromise.catch) volPromise.catch(() => {});
        }
      } catch { }
    }
    if (isEnded && onEnded) onEnded();
  };

  const handleCardClick = () => {
    // On mobile, taps are handled by onTouchEnd to bypass slick's swipe detection
    if (isMobile && touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }
    if (!hasYouTubeConsent()) {
      setShowModal(true);
      return;
    }
    runAction();
  };

  const handleAccept = () => {
    setYouTubeConsentAccepted();
    setShowModal(false);
    runAction();
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
    touchHandledRef.current = false;
  };

  const handleTouchEnd = (e) => {
    if (!touchStartRef.current) return;
    const t = e.changedTouches[0];
    const dx = Math.abs(t.clientX - touchStartRef.current.x);
    const dy = Math.abs(t.clientY - touchStartRef.current.y);
    touchStartRef.current = null;
    // Only treat as tap if finger barely moved (< 10px in any direction)
    if (dx < 10 && dy < 10) {
      touchHandledRef.current = true;
      e.preventDefault(); // prevent slick's synthetic click from double-firing
      if (!hasYouTubeConsent()) {
        setShowModal(true);
        return;
      }
      runAction();
    }
  };

  return (
    <>
      <div
        className="yt-card"
        ref={containerRef}
        onClick={handleCardClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="yt-card-inner">
          <div className="yt-thumb-wrapper">
            {shouldShowIframe ? (
              <YouTube
                key={videoId}
                videoId={videoId}
                opts={{
                  width: "100%",
                  height: "100%",
                  playerVars: {
                    autoplay: 1,
                    mute: 1,
                    playsinline: 1,
                    rel: 0,
                    modestbranding: 1,
                    enablejsapi: 1,
                  },
                }}
                onReady={(e) => {
                  try {
                    playerRef.current = e?.target;
                    const playPromise = e?.target?.playVideo?.();
                    if (playPromise && playPromise.catch) playPromise.catch(() => {});
                  } catch {}
                }}
                onStateChange={handleStateChange}
                className="yt-fill"
                iframeClassName="yt-iframe"
                style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}
              />
            ) : (
              <>
                {!imageLoaded && (
                  <div className="yt-card-spinner-container">
                    <div className="yt-card-spinner" />
                  </div>
                )}
                <img
                  src={thumbnailUrl}
                  alt="Video thumbnail"
                  className={`yt-thumb-image ${imageLoaded ? "loaded" : "loading"}`}
                  onLoad={() => setImageLoaded(true)}
                  loading="lazy"
                  decoding="async"
                />
                {imageLoaded && (
                  <>
                    <div className="yt-thumb-overlay" />
                    <div className="yt-play-icon" aria-hidden="true">
                      <span className="play-glyph" />
                    </div>
                    {channelName && (
                      <div className="yt-metadata-overlay" title={channelName}>
                        <span className="yt-channel-name">{channelName}</span>
                      </div>
                    )}
                  </>
                )}

              </>
            )}
          </div>
        </div>
      </div>
      <YouTubeConsentModal
        open={showModal}
        onAccept={handleAccept}
        onCancel={handleCancel}
      />
    </>
  );
}

export function YouTubeHorizontalRowExample() {
  const scrollRef = useRef(null);
  const videos = [
    { id: "dQw4w9WgXcQ" },
    { id: "kxopViU98Xo" },
    { id: "3JZ_D3ELwOQ" },
    { id: "9bZkp7q19f0" },
    { id: "RgKAFK5djSk" },
  ];

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const amount = direction === "left" ? -container.clientWidth : container.clientWidth;
    container.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <div className="video-row">
      <button
        type="button"
        className="scroll-arrow left"
        onClick={() => scroll("left")}
      >
        <span className="scroll-arrow__shape" aria-hidden="true" />
      </button>
      <div className="scroll-container" ref={scrollRef}>
        <div className="d-flex gap-3">
          {videos.map((video) => (
            <div key={video.id} className="video-card">
              <YouTubeCard videoId={video.id} />
            </div>
          ))}
        </div>
      </div>
      <button
        type="button"
        className="scroll-arrow right"
        onClick={() => scroll("right")}
      >
        <span className="scroll-arrow__shape" aria-hidden="true" />
      </button>
    </div>
  );
}
